import React from 'react'
import { RouteComponentProps } from '@reach/router'
import useImportDetails, { Event, IEmployeeImportFailedWithId } from './hooks/useImportDetails'
import Appbar from '../Appbar'
import {
  IconButton,
  Flex,
  FilterButton,
  PageContent,
  Icon,
  NewGridTable,
  GridTableStatusEnum,
  IGridTableColumn,
  Popover,
  PrettyDate,
} from '@ui'
import { IEmployeeImportVersion } from '@api'
import { Checkbox, Tag, Classes, Position } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { startCase, omit } from 'lodash'
import queryString from 'query-string'
import { DateUtil } from '@utils'
import css from './importDetails.module.scss'

// typeguard
function isIEmployeeImportVersion(
  item: IEmployeeImportVersion | IEmployeeImportFailedWithId
): item is IEmployeeImportVersion {
  return (item as IEmployeeImportVersion).event !== undefined
}

function placeholder(string = 'placeholder') {
  return <div className={Classes.SKELETON}>{string}</div>
}

export default function ImportDetails({
  id,
  navigate,
  location,
}: { id: string } & RouteComponentProps) {
  let filter
  if (location?.search) {
    filter = queryString.parse(location.search).filter
  }
  const {
    details,
    failed,
    loadMore,
    loadingMore,
    pageInfo,
    loading,
    events,
    toggleEvent,
    createdAt,
    importedBy,
    showFailed,
    setShowFailed,
    onReset,
  } = useImportDetails(id, filter as Event | undefined)

  const toggleShowFailed = () => setShowFailed(!showFailed)

  const columns: IGridTableColumn<IEmployeeImportVersion | IEmployeeImportFailedWithId>[] = [
    {
      label: 'Status',
      Cell: ({ cell }) => {
        if (!cell) return placeholder()

        if (isIEmployeeImportVersion(cell.data)) {
          switch (cell.data.event) {
            case 'created':
              return (
                <Tag intent="success" minimal round>
                  Created
                </Tag>
              )

            case 'updated':
              return (
                <Popover interactionKind="hover" content={<ChangedValues employee={cell.data} />}>
                  <Tag intent="primary" minimal round>
                    Updated
                  </Tag>
                </Popover>
              )

            case 'destroyed':
              return (
                <Tag intent="warning" minimal round>
                  Removed
                </Tag>
              )

            default:
              return null
          }
        } else {
          return (
            <Popover
              interactionKind={'hover'}
              content={<FailedReasons failedEmployee={cell.data} />}>
              <Tag intent="danger" minimal round>
                Failed
              </Tag>
            </Popover>
          )
        }
      },
    },
    {
      label: 'Employee ID',
      Cell: ({ cell }) => {
        if (!cell) return placeholder('short')

        return (
          <Tooltip2
            content={cell.data.employee.externalId}
            className="w-full"
            lazy
            placement={Position.BOTTOM}>
            <div className="truncate w-full">{cell.data.employee.externalId}</div>
          </Tooltip2>
        )
      },
    },
    {
      label: 'Full Name',
      Cell: ({ cell }) => {
        if (!cell) return placeholder('short')

        return (
          <Tooltip2
            content={cell.data.employee.name}
            className="w-full"
            lazy
            placement={Position.BOTTOM}>
            <div className="truncate w-full">{cell.data.employee.name}</div>
          </Tooltip2>
        )
      },
    },
    {
      label: 'Email',
      Cell: ({ cell }) => {
        if (!cell) return placeholder('short')

        return (
          <Tooltip2
            content={cell.data.employee.email}
            className="w-full"
            lazy
            placement={Position.BOTTOM}>
            <div className="truncate w-full">{cell.data.employee.email}</div>
          </Tooltip2>
        )
      },
    },
    {
      label: 'Phone',
      Cell: ({ cell }) => {
        if (!cell) return placeholder('short')

        return (
          <Tooltip2
            content={cell.data.employee.phone}
            className="w-full"
            lazy
            placement={Position.BOTTOM}>
            <div className="truncate w-full">{cell.data.employee.phone}</div>
          </Tooltip2>
        )
      },
    },
    {
      label: 'Department',
      Cell: ({ cell }) => {
        if (!cell) return placeholder('short')

        return (
          <Tooltip2
            content={cell.data.employee.dept?.name || ''}
            className="w-full"
            lazy
            placement={Position.BOTTOM}>
            <div className="truncate w-full">{cell.data.employee.dept?.name || ''}</div>
          </Tooltip2>
        )
      },
    },
    {
      label: 'Team',
      Cell: ({ cell }) => {
        if (!cell) return placeholder('short')

        return (
          <Tooltip2
            content={cell.data.employee.team?.name || ''}
            className="w-full"
            lazy
            placement={Position.BOTTOM}>
            <div className="truncate w-full">{cell.data.employee.team?.name || ''}</div>
          </Tooltip2>
        )
      },
    },
    {
      label: 'Reporting to',
      Cell: ({ cell }) => {
        if (!cell) return placeholder('short')

        return (
          <Tooltip2
            content={cell.data.employee.reportingTo}
            className="w-full"
            lazy
            placement={Position.BOTTOM}>
            <div className="truncate w-full">{cell.data.employee.reportingTo}</div>
          </Tooltip2>
        )
      },
    },
    {
      label: 'Location',
      Cell: ({ cell }) => {
        if (!cell) return placeholder('short')

        return (
          <Tooltip2
            content={cell.data.employee.location}
            className="w-full"
            lazy
            placement={Position.BOTTOM}>
            <div className="truncate w-full">{cell.data.employee.location}</div>
          </Tooltip2>
        )
      },
    },
    {
      label: 'Title',
      Cell: ({ cell }) => {
        if (!cell) return placeholder('short')

        return (
          <Tooltip2
            content={cell.data.employee.title}
            className="w-full"
            lazy
            placement={Position.BOTTOM}>
            <div className="truncate w-full">{cell.data.employee.title}</div>
          </Tooltip2>
        )
      },
    },
    {
      label: 'Type',
      Cell: ({ cell }) => {
        if (!cell) return placeholder('short')

        return (
          <Tooltip2
            content={cell.data.employee.employmentType}
            className="w-full"
            lazy
            placement={Position.BOTTOM}>
            <div className="truncate w-full">{cell.data.employee.employmentType}</div>
          </Tooltip2>
        )
      },
    },
    {
      label: 'Badge ID',
      Cell: ({ cell }) => {
        if (!cell) return placeholder('short')

        return (
          <Tooltip2
            content={cell.data.employee.badge}
            className="w-full"
            lazy
            placement={Position.BOTTOM}>
            <div className="truncate w-full">{cell.data.employee.badge}</div>
          </Tooltip2>
        )
      },
    },
  ]

  const formatBuildings = (items: (IEmployeeImportVersion | IEmployeeImportFailedWithId)[]) => {
    return items.map((i) => ({
      data: i,
    }))
  }

  const formatedBuildings = formatBuildings(
    !events.length
      ? showFailed
        ? failed
        : [...failed, ...details]
      : showFailed
      ? [...failed, ...details]
      : details
  )

  return (
    <>
      <Appbar
        title={
          <Flex align="center" justify="center">
            <span style={{ marginRight: 8 }}>
              <Tooltip2 content="Back to Import History">
                <IconButton
                  onClick={() => navigate?.('/employees/history')}
                  iconName="chevron-left"
                  minimal
                />
              </Tooltip2>
            </span>
            <Flex direction="column">
              <span style={{ fontSize: 14 }}>Import Details</span>
              <span
                style={{
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  color: 'var(--grey1)',
                }}>
                {createdAt && (
                  <>
                    <PrettyDate value={createdAt} timeStringFormat={'YYYY-MM-DD hh:mm:ss'} /> @
                    {DateUtil(createdAt).format('h:mmA')} by {importedBy}
                  </>
                )}
              </span>
            </Flex>
          </Flex>
        }
      />
      <PageContent>
        <Toolbar
          events={events}
          toggleEvent={toggleEvent}
          showFailed={showFailed}
          toggleShowFailed={toggleShowFailed}
          onReset={onReset}
        />
        <NewGridTable<IEmployeeImportVersion | IEmployeeImportFailedWithId>
          sticky={true}
          status={
            loading
              ? GridTableStatusEnum.LOADING
              : loadingMore
              ? GridTableStatusEnum.PAGING
              : GridTableStatusEnum.IDLE
          }
          columns={columns}
          data={formatedBuildings}
          page={{
            hasNextPage: showFailed && !events.length ? false : pageInfo?.hasNextPage || false,
            endCursor: pageInfo?.endCursor || '',
            onNextPage: loadMore,
          }}
          headerTop={52}
        />
      </PageContent>
    </>
  )
}

function Toolbar({
  events,
  toggleEvent,
  showFailed,
  toggleShowFailed,
  onReset,
}: {
  events: Omit<Event, 'failed'>[]
  toggleEvent: (event: 'created' | 'updated' | 'destroyed') => void
  showFailed: boolean
  toggleShowFailed: () => void
  onReset: () => void
}) {
  const filterCount = events.length + +showFailed

  const filters = [
    {
      title: 'Created',
      onChange: () => toggleEvent('created'),
      checked: !!events.find((e) => e === 'created'),
    },
    {
      title: 'Updated',
      onChange: () => toggleEvent('updated'),
      checked: !!events.find((e) => e === 'updated'),
    },
    {
      title: 'Removed',
      onChange: () => toggleEvent('destroyed'),
      checked: !!events.find((e) => e === 'destroyed'),
    },
    {
      title: 'Failed',
      onChange: () => toggleShowFailed(),
      checked: showFailed,
    },
  ]

  return (
    <Flex className={css.importDetailsToolbar}>
      <Popover
        minimal
        content={
          <div style={{ padding: 10 }}>
            <table>
              <tbody>
                {filters.map(({ title, onChange, checked }, i) => (
                  <tr key={i}>
                    <td>
                      <Checkbox onChange={onChange} checked={checked} style={{ marginLeft: 10 }} />
                    </td>
                    <td style={{ height: 30 }}>{title}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }>
        <FilterButton filterCount={filterCount} />
      </Popover>
      {!!filterCount && (
        <Flex align="baseline" style={{ marginLeft: 10 }}>
          <span style={{ color: 'var(--grey1)' }}>filtered results</span>
          <IconButton
            minimal
            tooltip="Clear filters"
            iconName="times"
            style={{ marginLeft: 4 }}
            onClick={onReset}
          />
        </Flex>
      )}
    </Flex>
  )
}

interface ChangedValue {
  change: { oldValue: string | null; newValue: string | null }
  field: string
}

function ChangedValues({ employee }: { employee: IEmployeeImportVersion }) {
  if (extraFieldChanged(employee)) {
    return <div style={{ padding: 20 }}>Extra Field updated</div>
  }

  const columns: IGridTableColumn<ChangedValue & { id: string }>[] = [
    {
      label: 'Field',
      Cell: ({ cell }) => {
        if (!cell) return null

        return <div>{cell.data.field}</div>
      },
    },
    {
      label: 'Change',
      colSpan: 3,
      Cell: ({ cell }) => {
        if (!cell) return null

        return (
          <div>
            {`${cell.data.change.oldValue}`}
            <Icon name="long-arrow-right" style={{ margin: '0 10px' }} />
            {`${cell.data.change.newValue}`}
          </div>
        )
      },
    },
  ]

  const data: ChangedValue[] = Object.entries(employee).reduce<ChangedValue[]>(
    (acc, [key, value]) => {
      let capKey = startCase(key)
      if (
        !value ||
        key === '__typename' ||
        key === 'employee' ||
        key === 'id' ||
        key === 'event' ||
        value.changed === false
      )
        return acc
      if (key === 'departmentName') capKey = 'Department'
      if (key === 'teamName') capKey = 'Team'
      return [
        ...acc,
        {
          field: capKey,
          change: { oldValue: value.oldValue, newValue: value.value },
        },
      ]
    },
    []
  )

  const format = (items: ChangedValue[]) => {
    return items.map((i) => ({
      data: { id: i.field, ...i },
    }))
  }

  const formated = format(data)

  return (
    <div style={{ padding: 5, background: 'var(--white)' }}>
      <NewGridTable<ChangedValue & { id: string }>
        sticky={false}
        status={GridTableStatusEnum.IDLE}
        columns={columns}
        data={formated}
      />
    </div>
  )
}

function extraFieldChanged(employee: IEmployeeImportVersion) {
  let extraFieldChange = true

  Object.entries(employee).forEach(([key, value]) => {
    if (value.changed) {
      extraFieldChange = false
    }
  })
  return extraFieldChange
}

function FailedReasons({ failedEmployee }: { failedEmployee: IEmployeeImportFailedWithId }) {
  if (Object.values(omit(failedEmployee.errors, '__typename')).every((value) => value === null)) {
    return (
      <div
        style={{
          maxWidth: 350,
          padding: 20,
          zIndex: 100,
        }}>
        Unknown Error
      </div>
    )
  }

  return (
    <Flex direction="column" style={{ padding: 20 }}>
      {Object.entries(failedEmployee.errors).map(([key, value]) => {
        const capKey = startCase(key)
        if (!value || key === '__typename') return null
        return (
          <span>
            "{capKey}" {value}
          </span>
        )
      })}
    </Flex>
  )
}
